import { z } from 'zod';

export const AutomationScope = z.enum([
  'mail.read',
  'mail.modify',
  'drafts.write',
  'mail.send',
  'contacts.read',
  'contacts.write',
  'tasks.read',
  'tasks.write',
  'notes.read',
  'notes.write',
]);
export type AutomationScope = z.infer<typeof AutomationScope>;

export const AutomationTokenCreate = z.object({
  name: z.string().trim().min(1).max(80),
  currentPassword: z.string().min(1).max(1024),
  scopes: z
    .array(AutomationScope)
    .min(1)
    .max(AutomationScope.options.length)
    .refine((items) => new Set(items).size === items.length, 'Scopes must be unique'),
  expiresInDays: z.number().int().min(1).max(3_650).nullable().default(90),
});
export type AutomationTokenCreate = z.infer<typeof AutomationTokenCreate>;

export const AutomationToken = z.object({
  id: z.number().int().positive(),
  name: z.string(),
  prefix: z.string(),
  scopes: z.array(AutomationScope),
  createdAt: z.number().int().nonnegative(),
  expiresAt: z.number().int().nonnegative().nullable(),
  lastUsedAt: z.number().int().nonnegative().nullable(),
  revokedAt: z.number().int().nonnegative().nullable(),
});
export type AutomationToken = z.infer<typeof AutomationToken>;

export const AutomationTokenList = z.array(AutomationToken);
export type AutomationTokenList = z.infer<typeof AutomationTokenList>;

export const AutomationTokenCreateResponse = z.object({
  token: z.string(),
  item: AutomationToken,
});
export type AutomationTokenCreateResponse = z.infer<typeof AutomationTokenCreateResponse>;

export const AutomationTokenRevokeResponse = z.object({
  ok: z.literal(true),
  revoked: z.number().int().nonnegative(),
});
export type AutomationTokenRevokeResponse = z.infer<typeof AutomationTokenRevokeResponse>;

export const AutomationActivity = z.object({
  id: z.number().int().positive(),
  tokenId: z.number().int().positive().nullable(),
  tokenName: z.string().nullable(),
  method: z.string(),
  route: z.string(),
  statusCode: z.number().int(),
  createdAt: z.number().int().nonnegative(),
});
export type AutomationActivity = z.infer<typeof AutomationActivity>;

export const AutomationActivityList = z.array(AutomationActivity);
export type AutomationActivityList = z.infer<typeof AutomationActivityList>;

export const AutomationApiInfo = z.object({
  name: z.literal('Wintermute Automation API'),
  version: z.literal('v1'),
  user: z.object({
    id: z.number().int().positive(),
    username: z.string(),
    displayName: z.string(),
  }),
  token: z.object({
    id: z.number().int().positive(),
    name: z.string(),
    scopes: z.array(AutomationScope),
    expiresAt: z.number().int().nonnegative().nullable(),
  }),
  documentation: z.literal('/api/v1/openapi.json'),
});
export type AutomationApiInfo = z.infer<typeof AutomationApiInfo>;
